import { Trans } from "@lingui/macro";
import { useIsomorphicLayoutEffect } from "@react-hookz/web";
import {
  UiBox,
  UiDivider,
  UiLink,
  UiModuleLoader,
  UiTypography,
} from "@smp/ui";
import {
  IconPaint,
  IconPalette,
  IconPencil,
  IconPlus,
  IconStack,
  IconStar,
  IconTexture,
  IconTriangleSquareCircle,
  IconTypography,
} from "@tabler/icons-react";
import { useMemo, useRef, useState } from "react";

import { TemplateCard } from "@/components/features/polotno_templates/components";
import { useContextPolotnoTemplates } from "@/components/features/polotno_templates/context";
import { useTemplateVirtualizer } from "@/components/features/polotno_templates/hooks";
import { WidgetLink } from "@/components/features/polotno_templates/screens/viewport/components/widget_link";
import { useQueryPolotnoTemplates } from "@/queries";

import { useStyles } from "./nothing_found.css";

export const NothingFound = () => {
  const classes = useStyles();
  const parentRef = useRef<HTMLDivElement | null>(null);
  const [headerRef, setHeaderRef] = useState<HTMLDivElement | null>(null);
  const [scrollMargin, setScrollMargin] = useState(0);
  const {
    state: { isWidget, selectedTags },
    actions: { onCreateNew },
    refs,
  } = useContextPolotnoTemplates();

  const { data: polotnoTemplatesData, isLoading: isTemplatesLoading } =
    useQueryPolotnoTemplates({
      tags__slug__in: selectedTags,
    });

  const polotnoTemplates = useMemo(
    () => polotnoTemplatesData?.pages.flatMap(({ results }) => results),
    [polotnoTemplatesData?.pages],
  );

  const templateItems = useMemo(() => {
    const results: Parameters<typeof useTemplateVirtualizer>[0] = [];

    if (polotnoTemplates?.length) {
      polotnoTemplates.forEach((template) => {
        results.push({
          key: `template-${template.id}`,
          payload: template,
          type: "template",
        });
      });
    }

    return results;
  }, [polotnoTemplates]);

  const { virtualizer, calculateStyles, getTotalSizeCustom } =
    useTemplateVirtualizer(templateItems, parentRef.current, {
      scrollMargin,
    });

  const virtualItems = virtualizer.getVirtualItems();

  useIsomorphicLayoutEffect(() => {
    if (headerRef) {
      setScrollMargin(headerRef?.clientHeight);

      const resizeObserver = new ResizeObserver(() => {
        setScrollMargin(headerRef?.clientHeight || 0);
      });

      resizeObserver.observe(headerRef);
    }
  }, [headerRef]);

  useIsomorphicLayoutEffect(() => {
    if (refs?.virtualizerTemplates && virtualizer.scrollElement) {
      refs.virtualizerTemplates(virtualizer);
    }
  }, [refs, refs?.virtualizerTemplates, virtualizer]);

  if (isTemplatesLoading) {
    return <UiModuleLoader />;
  }

  return (
    <UiBox className={classes.container} ref={parentRef}>
      <UiBox
        className={classes.scroller}
        style={{
          height: !!virtualItems.length
            ? `${getTotalSizeCustom() + 20}px`
            : undefined,
        }}
      >
        {isWidget && <WidgetLink />}
        <UiBox ref={setHeaderRef}>
          <UiBox className={classes.wrapper}>
            <UiBox>
              <UiBox
                justifyContent="space-evenly"
                className={classes.icon_wrapper}
              >
                <IconTypography />
                <IconStack />
                <IconStar />
                <IconPalette />
              </UiBox>
              <UiTypography className={classes.text}>
                {!!polotnoTemplates?.length ? (
                  <Trans>
                    We don't have a template that fits all the criteria yet, but
                    we managed to find similar ones. You can also
                  </Trans>
                ) : (
                  <Trans>
                    We don't have a template that matches your request yet, but
                    you can
                  </Trans>
                )}
                <br />
                <UiLink onClick={onCreateNew}>
                  {!!polotnoTemplates?.length ? (
                    <Trans>create your own!</Trans>
                  ) : (
                    <Trans>try creating your own!</Trans>
                  )}
                </UiLink>
              </UiTypography>
              <UiBox
                justifyContent="space-around"
                className={classes.icon_wrapper}
              >
                <IconTexture />
                <IconTriangleSquareCircle />
                <IconPlus />
                <IconPaint />
                <IconPencil />
              </UiBox>
            </UiBox>
          </UiBox>
          <UiBox p={4}>
            <UiDivider />
          </UiBox>
        </UiBox>

        {parentRef.current &&
          !!virtualItems.length &&
          virtualItems.map((virtualItem) => {
            const item = templateItems[virtualItem.index];

            if (item.type !== "template") {
              return null;
            }

            return (
              <UiBox
                style={calculateStyles(virtualItem)}
                key={virtualItem.key.toString()}
              >
                <div
                  ref={virtualizer.measureElement}
                  data-index={virtualItem.index}
                >
                  <TemplateCard template={item.payload} />
                </div>
              </UiBox>
            );
          })}
      </UiBox>
    </UiBox>
  );
};
